import PAButton from "component/design-system/buttons/PAButton";
import PAImage from "component/design-system/images/PAImage";
import PAText from "component/design-system/typography/PAText/PAText";
import LearnMore from "container/about-us/LearnMore";
import { useTranslation } from "next-i18next";
import { useRef } from "react";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import styles from "./careers.module.scss";

function CareersContainer() {
  const { t } = useTranslation("static-pages");
  const rolesSectionRef = useRef(null);

  const jobListings = [
    {
      title: t("Frontend Engineer"),
      type: "FULL TIME",
      location: "TORONTO",
      description: t("frontend_description"),
    },
    {
      title: t("Backend Engineer"),
      type: "FULL TIME",
      location: "TORONTO",
      description: t("backend_description"),
    },
    {
      title: t("Data Analyst"),
      type: "FULL TIME",
      location: "MONTREAL",
      description: t("data_analyst_description"),
    },
    {
      title: t("Product Manager"),
      type: "FULL TIME",
      location: "TORONTO",
      description: t("product_manager_description"),
    },
  ];

  const handleScrollToRoles = () => {
    rolesSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className={styles.careers_container}>
      <div className={`${styles.section} ${styles.top_section}`}>
        <PAImage
          src={getRelativeImagePath("/cms/pubicImages/images/careers-banner.webp")}
          alt="Careers"
          className={styles.careers_banner_image}
          height={570}
        />
        <div className={styles.top_section_text}>
          <PAText component="h1" bold className={`m-t-30 ${styles.section_header}`}>
            {t("We're making a positive, sustainable change in the world of auto repair. Together.")}
          </PAText>

          <PAText>
            {t("parts_tech_mission")}
          </PAText>

          <PAButton bold className={styles.top_section_button} onClick={handleScrollToRoles}>
            {t("See all roles")}
          </PAButton>
        </div>
      </div>

      <div className={`${styles.section} ${styles.quote_section}`}>
        <div className={styles.quote_container}>
          <PAText className={styles.quotation_mark}>“</PAText>
          <PAText component="p" className={styles.quote}>
            {t("about_quote_1")}
          </PAText>
          <PAText component="p" className={styles.author}>
            {t(`SHERDEEP SINGH - ${t("FOUNDER & CEO")}`)}
          </PAText>
        </div>

        <div className={styles.line_break} />
      </div>

      <div className={`${styles.section} ${styles.values_section}`}>
        <PAText
          component="h2"
          bold
          className={styles.section_header}
        >
          {t("Our Values")}
        </PAText>
        <div className={styles.value_container}>
          <div className={styles.value}>
            <PAText bold className={styles.value_step}>01</PAText>
            <div className={styles.value_text}>
              <PAText bold className={styles.value_title}>
                {t("Right to repair")}
              </PAText>
              <PAText className={styles.value_description}>
                {t("People should have access to information, diagnostics, tools and parts necessary to do repairs at a reasonable price.")}
              </PAText>
            </div>
          </div>
          <div className={styles.value}>
            <PAText bold className={styles.value_step}>02</PAText>
            <div className={styles.value_text}>
              <PAText bold className={styles.value_title}>
                {t("Make a difference")}
              </PAText>
              <PAText className={styles.value_description}>
                {t("We strive to make a difference in the lives of our colleagues and customers, and for the well-being of our planet.")}
              </PAText>
            </div>
          </div>
          <div className={styles.value}>
            <PAText bold className={styles.value_step}>03</PAText>
            <div className={styles.value_text}>
              <PAText bold className={styles.value_title}>
                {t("Honesty")}
              </PAText>
              <PAText className={styles.value_description}>
                {t("We believe transparency builds employee confidence and a sense of ownership.")}
              </PAText>
            </div>
          </div>
          <div className={styles.value}>
            <PAText bold className={styles.value_step}>04</PAText>
            <div className={styles.value_text}>
              <PAText bold className={styles.value_title}>
                {t("Freedom")}
              </PAText>
              <PAText className={styles.value_description}>
                {t("We recognise that each of us has a personal preference for how and when to work.")}
              </PAText>
            </div>
          </div>
          <div className={styles.value}>
            <PAText bold className={styles.value_step}>05</PAText>
            <div className={styles.value_text}>
              <PAText bold className={styles.value_title}>
                {t("Learning & Growth")}
              </PAText>
              <PAText className={styles.value_description}>
                {t("We encourage dedicated learning times and have one size-fits-one growth plans.")}
              </PAText>
            </div>
          </div>
          <div className={styles.value}>
            <PAText bold className={styles.value_step}>06</PAText>
            <div className={styles.value_text}>
              <PAText bold className={styles.value_title}>
                {t("Having fun")}
              </PAText>
              <PAText className={styles.value_description}>
                {t("Building Parts Avatar should be a fun and memorable experience. Not a grind.")}
              </PAText>
            </div>
          </div>
        </div>

        <div className={styles.line_break} />
      </div>

      <div className={`${styles.section} ${styles.employee_section}`}>

        <PAText
          component="h2"
          bold
          className={styles.section_header}
        >
          {t("What our employees are saying")}
        </PAText>

        <div className={styles.text_image_section}>
          <div className={styles.text_left}>
            <PAText className={styles.quotation_mark}>“</PAText>
            <PAText component="p" className={styles.quote}>
              {t("employee_quote")}
            </PAText>
            <PAText component="p" className={styles.employee_author}>
              VITALII DUMMA
            </PAText>
          </div>
          <div className={styles.image}>
            <PAImage
              src={getRelativeImagePath("/cms/pubicImages/images/careers-testimony.webp")}
              width={200}
              height={400}
              alt="Employee"
            />
          </div>
        </div>
      </div>

      <div className={`${styles.section} ${styles.fullwidth_section} ${styles.roles_section}`} ref={rolesSectionRef}>
        <div className={styles.fullwidth_container}>
          <PAText
            component="h2"
            bold
            className={styles.section_header}
          >
            {t("Ready to join our team?")}
          </PAText>

          <PAText>
            {t("Apply here for our current openings")}
          </PAText>

          <div className={styles.job_listings}>
            {jobListings.map((job, index) => (
              <div key={index} className={styles.job_card}>
                <PAText component="h3" className={styles.job_title}>{job.title}</PAText>
                <PAText component="p" className={styles.job_info}>
                  {`${t(job.type)} | ${job.location}`}
                </PAText>
                <PAText component="p" className={styles.job_description}>{job.description}</PAText>
                <PAButton className={styles.button} onClick={() => window.open("https://form.asana.com/?d=1203096050556854&k=7j0KrB8nes6z8UhsV_oxOA")}>{t("Apply Now")}</PAButton>
                {[0, 1].includes(index) && <hr className={styles.divider} />}
              </div>
            ))}
          </div>
        </div>
      </div>
      <LearnMore />
    </div>
  );
}

export default CareersContainer;
